import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, ViewChild
} from '@angular/core';
import {
  MyCalendarItem, MyCalendarSlot, MyCalendarTooltip, coversDay, minutesOf, parseDateKey, timeOf, tooltipFor
} from '../my-calendar.models';
import { RESOURCE_TOOLTIP_POSITIONS } from '../../shared/assignment-tooltip/resource-assignment-tooltip.component';

interface AllDayBar {
  item: MyCalendarItem;
  column: number;
  span: number;
  lane: number;
}

interface TimedBlock {
  item: MyCalendarItem;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

const HOUR_HEIGHT = 44;
const SNAP_MINUTES = 30;

/**
 * Wochenansicht von „Mein Kalender“: Tage als Spalten, Stunden als Zeilen.
 * Ganz- und mehrtägige Einträge stehen in der Ganztagszeile. Klick bzw. Ziehen
 * auf eine freie Fläche wählt einen Zeitraum zum Anlegen eines Termins.
 */
@Component({
  selector: 'app-resource-my-calendar-week',
  templateUrl: './resource-my-calendar-week.component.html',
  styleUrls: ['../my-calendar-items.css', './resource-my-calendar-week.component.css']
})
export class ResourceMyCalendarWeekComponent implements OnChanges, AfterViewInit {
  @Input() days: string[] = [];
  @Input() items: MyCalendarItem[] = [];
  @Input() today = '';
  /** false = Freiflächen sind nicht anklickbar (z. B. während Speichern). */
  @Input() selectable = true;
  @Output() itemClick = new EventEmitter<MyCalendarItem>();
  @Output() slotSelect = new EventEmitter<MyCalendarSlot>();

  @ViewChild('scroll') private scroll?: ElementRef<HTMLElement>;

  readonly hours = Array.from({ length: 24 }, (_, i) => i);
  readonly hourHeight = HOUR_HEIGHT;
  readonly tooltipPositions = RESOURCE_TOOLTIP_POSITIONS;

  /** Eintrag unter der Maus (Tooltip wie in der Teamplanung). */
  hoverKey: string | null = null;

  tooltip(item: MyCalendarItem): MyCalendarTooltip { return tooltipFor(item); }

  /** Kurze Termine (unter ca. 45 Minuten): nur der Titel in einer Zeile. */
  isShort(block: { height: number }): boolean { return block.height < 34; }

  allDayBars: AllDayBar[] = [];
  allDayLanes = 0;
  blocksByDay: TimedBlock[][] = [];

  /** Laufende Auswahl (Spalte + Minuten). */
  selection: { day: number; from: number; to: number } | null = null;
  private selectionStart = 0;
  private selectionColumn: HTMLElement | null = null;

  ngOnChanges(): void {
    this.buildAllDay();
    this.blocksByDay = this.days.map(day => this.buildTimed(day));
  }

  ngAfterViewInit(): void {
    // Arbeitstag sichtbar: auf 07:00 scrollen.
    if (this.scroll) this.scroll.nativeElement.scrollTop = 7 * HOUR_HEIGHT;
  }

  dayLabel(day: string): string {
    return parseDateKey(day).toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  isWeekend(day: string): boolean {
    const d = parseDateKey(day).getDay();
    return d === 0 || d === 6;
  }

  timeLabel(item: MyCalendarItem): string {
    return `${item.startTime} – ${item.endTime}`;
  }

  trackItem(_i: number, entry: { item: MyCalendarItem }): string { return entry.item.key; }

  // ---- Layout ---------------------------------------------------------------------

  private buildAllDay(): void {
    const first = this.days[0];
    const last = this.days[this.days.length - 1];
    const bars: AllDayBar[] = [];
    const laneEnds: number[] = [];
    const items = this.items
      .filter(i => i.allDay && i.startDate <= last && i.endDate >= first)
      .sort((a, b) => a.startDate.localeCompare(b.startDate) || b.endDate.localeCompare(a.endDate));

    for (const item of items) {
      const column = Math.max(0, this.days.indexOf(item.startDate < first ? first : item.startDate));
      const endColumn = this.days.indexOf(item.endDate > last ? last : item.endDate);
      let lane = laneEnds.findIndex(end => end < column);
      if (lane < 0) { lane = laneEnds.length; laneEnds.push(endColumn); } else { laneEnds[lane] = endColumn; }
      bars.push({ item, column, span: endColumn - column + 1, lane });
    }
    this.allDayBars = bars;
    this.allDayLanes = laneEnds.length;
  }

  private buildTimed(day: string): TimedBlock[] {
    const items = this.items
      .filter(i => !i.allDay && coversDay(i, day))
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
    const blocks: TimedBlock[] = [];
    const laneEnds: number[] = [];
    for (const item of items) {
      const start = minutesOf(item.startTime);
      const end = Math.max(start + 15, minutesOf(item.endTime));
      let lane = laneEnds.findIndex(e => e <= start);
      if (lane < 0) { lane = laneEnds.length; laneEnds.push(end); } else { laneEnds[lane] = end; }
      blocks.push({ item, top: start / 60 * HOUR_HEIGHT, height: (end - start) / 60 * HOUR_HEIGHT, lane, lanes: 1 });
    }
    // Nebeneinander, wenn sich Einträge überschneiden.
    for (const block of blocks) block.lanes = Math.max(1, laneEnds.length);
    return blocks;
  }

  // ---- Zeitraum auswählen ------------------------------------------------------------

  onColumnPointerDown(event: PointerEvent, dayIndex: number): void {
    if (!this.selectable || event.button !== 0) return;
    if ((event.target as HTMLElement).closest('.event')) return;
    event.preventDefault();
    this.selectionColumn = event.currentTarget as HTMLElement;
    this.selectionStart = this.minutesAt(event.clientY);
    this.selection = { day: dayIndex, from: this.selectionStart, to: this.selectionStart + SNAP_MINUTES };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.selection) return;
    const current = this.minutesAt(event.clientY);
    this.selection = {
      day: this.selection.day,
      from: Math.min(this.selectionStart, current),
      to: Math.max(this.selectionStart, current) + SNAP_MINUTES
    };
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    const selection = this.selection;
    if (!selection) return;
    this.selection = null;
    this.selectionColumn = null;
    // Einfacher Klick: eine Stunde vorschlagen.
    const to = selection.to - selection.from <= SNAP_MINUTES ? selection.from + 60 : selection.to;
    const day = this.days[selection.day];
    this.slotSelect.emit({
      startDate: day,
      startTime: timeOf(selection.from),
      endDate: day,
      endTime: to >= 24 * 60 ? '23:59' : timeOf(to)
    });
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.selection = null;
  }

  private minutesAt(clientY: number): number {
    if (!this.selectionColumn) return 0;
    const rect = this.selectionColumn.getBoundingClientRect();
    const minutes = (clientY - rect.top) / HOUR_HEIGHT * 60;
    return Math.max(0, Math.min(24 * 60 - SNAP_MINUTES, Math.floor(minutes / SNAP_MINUTES) * SNAP_MINUTES));
  }
}
